/**
 * Closing Stock Screen (4.4)
 * Inventory Module - Stock Reports
 * Based on Inventory.pdf specification
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Search, RefreshCw, Download, FileText, Filter, Calendar } from 'lucide-react';
import { closingStock } from '../../../services/api/inventory/inventoryService';

const ClosingStockScreen = ({ theme = 'dark' }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [totalValue, setTotalValue] = useState(0);
  const [filters, setFilters] = useState({
    division: '',
    department: '',
    category: '',
    brand: '',
    asOnDate: new Date().toISOString().split('T')[0]
  });

  const isDark = theme === 'dark';
  const bgColor = isDark ? 'bg-slate-900' : 'bg-gray-50';
  const cardBg = isDark ? 'bg-slate-800' : 'bg-white';
  const textColor = isDark ? 'text-white' : 'text-gray-900';
  const borderColor = isDark ? 'border-slate-700' : 'border-gray-200';
  const inputBg = isDark ? 'bg-slate-700' : 'bg-gray-50';

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await closingStock.getReport(filters);
      if (response?.data?.success) {
        setData(response.data.data || []);
        setTotalValue(response.data.totalValue || 0);
      }
    } catch (error) {
      console.error('Error loading closing stock:', error);
      // Use mock data on error
      setData([
        { ItemCode: 'ITM001', ItemName: 'Cotton Shirt - Blue', Qty: 120, Rate: 500, Value: 60000, Category: 'Shirts', Brand: 'Arrow' },
        { ItemCode: 'ITM002', ItemName: 'Denim Jeans - Black', Qty: 65, Rate: 1000, Value: 65000, Category: 'Jeans', Brand: 'Levis' },
        { ItemCode: 'ITM003', ItemName: 'Polo T-Shirt - White', Qty: 170, Rate: 300, Value: 51000, Category: 'T-Shirts', Brand: 'US Polo' },
        { ItemCode: 'ITM004', ItemName: 'Formal Shirt - Grey', Qty: 45, Rate: 600, Value: 27000, Category: 'Shirts', Brand: 'Van Heusen' },
      ]);
      setTotalValue(203000);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredData = data.filter(item =>
    Object.values(item).some(val =>
      String(val).toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const handleExport = (format) => {
    // Export functionality
    alert(`Exporting to ${format}...`);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
  };

  return (
    <div className={`min-h-screen ${bgColor} ${textColor} p-6`} data-testid="closing-stock-screen">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Closing Stock Report</h1>
          <p className="text-sm text-gray-400">Screen ID: 4.4 | Inventory Module - Stock Reports</p>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={loadData} 
            className="p-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white" 
            data-testid="refresh-btn"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
          <button 
            onClick={() => handleExport('excel')} 
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white"
            data-testid="export-excel-btn"
          >
            <Download size={18} />
            Excel
          </button>
          <button 
            onClick={() => handleExport('pdf')} 
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white"
            data-testid="export-pdf-btn"
          >
            <FileText size={18} />
            PDF
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className={`${cardBg} rounded-lg p-4 border ${borderColor}`}>
          <p className="text-sm text-gray-400">Total Items</p>
          <p className="text-2xl font-bold text-blue-400">{data.length}</p>
        </div>
        <div className={`${cardBg} rounded-lg p-4 border ${borderColor}`}>
          <p className="text-sm text-gray-400">Total Quantity</p>
          <p className="text-2xl font-bold text-emerald-400">{data.reduce((sum, item) => sum + (item.Qty || 0), 0)}</p>
        </div>
        <div className={`${cardBg} rounded-lg p-4 border ${borderColor}`}>
          <p className="text-sm text-gray-400">Total Stock Value</p>
          <p className="text-2xl font-bold text-orange-400">{formatCurrency(totalValue)}</p>
        </div>
        <div className={`${cardBg} rounded-lg p-4 border ${borderColor}`}>
          <p className="text-sm text-gray-400">As On Date</p>
          <p className="text-2xl font-bold text-purple-400">{filters.asOnDate}</p>
        </div>
      </div>

      {/* Filters */}
      <div className={`${cardBg} rounded-lg p-4 mb-6 border ${borderColor}`}>
        <div className="flex items-center gap-2 mb-4">
          <Filter size={18} className="text-gray-400" />
          <span className="font-medium">Filters</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1">As On Date</label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="date"
                value={filters.asOnDate}
                onChange={(e) => setFilters({...filters, asOnDate: e.target.value})}
                className={`w-full pl-10 pr-3 py-2 rounded-lg ${inputBg} border ${borderColor} ${textColor}`}
                data-testid="filter-date"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Division</label>
            <select 
              value={filters.division}
              onChange={(e) => setFilters({...filters, division: e.target.value})}
              className={`w-full px-3 py-2 rounded-lg ${inputBg} border ${borderColor} ${textColor}`}
              data-testid="filter-division"
            >
              <option value="">All Divisions</option>
              <option value="mens">Men's Wear</option>
              <option value="womens">Women's Wear</option>
              <option value="kids">Kids Wear</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Department</label>
            <select 
              value={filters.department}
              onChange={(e) => setFilters({...filters, department: e.target.value})}
              className={`w-full px-3 py-2 rounded-lg ${inputBg} border ${borderColor} ${textColor}`}
              data-testid="filter-department"
            >
              <option value="">All Departments</option>
              <option value="formal">Formal</option>
              <option value="casual">Casual</option>
              <option value="ethnic">Ethnic</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Category</label>
            <select 
              value={filters.category}
              onChange={(e) => setFilters({...filters, category: e.target.value})}
              className={`w-full px-3 py-2 rounded-lg ${inputBg} border ${borderColor} ${textColor}`}
              data-testid="filter-category"
            >
              <option value="">All Categories</option>
              <option value="shirts">Shirts</option>
              <option value="trousers">Trousers</option>
              <option value="tshirts">T-Shirts</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Brand</label>
            <select 
              value={filters.brand}
              onChange={(e) => setFilters({...filters, brand: e.target.value})}
              className={`w-full px-3 py-2 rounded-lg ${inputBg} border ${borderColor} ${textColor}`}
              data-testid="filter-brand"
            >
              <option value="">All Brands</option>
              <option value="arrow">Arrow</option>
              <option value="vanheusen">Van Heusen</option>
              <option value="levis">Levi's</option>
            </select>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className={`${cardBg} rounded-lg p-4 mb-6 border ${borderColor}`}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search by item code, name, category, brand..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`w-full pl-10 pr-4 py-2 rounded-lg ${inputBg} border ${borderColor} ${textColor}`}
            data-testid="search-input"
          />
        </div>
      </div>

      {/* Data Grid */}
      <div className={`${cardBg} rounded-lg border ${borderColor} overflow-hidden`}>
        {loading ? (
          <div className="p-8 text-center">
            <RefreshCw className="animate-spin mx-auto mb-2" size={24} />
            <p>Loading closing stock data...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="data-table">
              <thead className={isDark ? 'bg-slate-700' : 'bg-gray-100'}>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase">SL</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Item Code</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Item Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Category</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Brand</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase">Qty</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase">Rate</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase">Value</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((item, index) => (
                  <tr key={item.ItemCode || index} className={`border-t ${borderColor} hover:${isDark ? 'bg-slate-700' : 'bg-gray-50'}`}>
                    <td className="px-4 py-3">{index + 1}</td>
                    <td className="px-4 py-3 font-mono text-sm">{item.ItemCode}</td>
                    <td className="px-4 py-3 font-medium">{item.ItemName}</td>
                    <td className="px-4 py-3">{item.Category}</td>
                    <td className="px-4 py-3">{item.Brand}</td>
                    <td className="px-4 py-3 text-right font-medium">{item.Qty}</td>
                    <td className="px-4 py-3 text-right">{formatCurrency(item.Rate)}</td>
                    <td className="px-4 py-3 text-right font-bold text-emerald-400">{formatCurrency(item.Value)}</td>
                  </tr>
                ))}
                {filteredData.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-gray-400">No stock data found</td>
                  </tr>
                )}
              </tbody>
              <tfoot className={isDark ? 'bg-slate-700' : 'bg-gray-100'}>
                <tr>
                  <td colSpan={5} className="px-4 py-3 font-bold text-right">TOTAL:</td>
                  <td className="px-4 py-3 text-right font-bold">{data.reduce((sum, item) => sum + (item.Qty || 0), 0)}</td>
                  <td className="px-4 py-3"></td>
                  <td className="px-4 py-3 text-right font-bold text-emerald-400">{formatCurrency(totalValue)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClosingStockScreen;
